"use client";

import type { FontWeight } from "@homeinstead/schemas";
import type { ButtonVariantProps } from "@homeinstead/ui/components/Button";
import type { ButtonColorVariant, ButtonWidthVariant } from "./button-styles";

import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import {
  COLOR_VARIANT_MAP,
  FONT_WEIGHT_CLASS,
  WIDTH_VARIANT_CLASS,
} from "./button-styles";
import { useTrackedStepAdvance } from "./use-tracked-step-advance";

interface ContinueButtonProps {
  colorVariant?: ButtonColorVariant | null;
  /** Direct Button variant override — used for hero light styling (e.g. outline-light). */
  variant?: ButtonVariantProps["variant"];
  disabled?: boolean;
  fontWeight?: FontWeight | null;
  label?: string | null;
  onContinue: () => void;
  title?: string;
  widthVariant?: ButtonWidthVariant | null;
  /**
   * The analytics step_name for the "Lead Form Step" event — resolved by the
   * parent as step.metricsLabel ?? step.id. When omitted, analytics tracking
   * is skipped.
   */
  analyticsStepName?: string;
  /**
   * The analytics user_selection for the "Lead Form Step" event. Used when this
   * button advances a step whose selection is derived from persisted field values
   * rather than being owned by the input's own Continue button.
   * Field-keyed object; multi-select fields are arrays.
   */
  analyticsUserSelection?: Record<string, string | string[]>;
}

function ContinueButton({
  colorVariant,
  variant,
  disabled,
  fontWeight,
  label,
  onContinue,
  title,
  widthVariant,
  analyticsStepName,
  analyticsUserSelection,
}: ContinueButtonProps) {
  const handleClick = useTrackedStepAdvance({
    analyticsStepName,
    analyticsUserSelection,
    onContinue,
  });

  return (
    <Button
      className={cn(
        widthVariant
          ? WIDTH_VARIANT_CLASS[widthVariant]
          : WIDTH_VARIANT_CLASS.full,
        fontWeight && FONT_WEIGHT_CLASS[fontWeight],
      )}
      disabled={disabled}
      onClick={handleClick}
      title={title}
      type="button"
      variant={
        variant ?? (colorVariant ? COLOR_VARIANT_MAP[colorVariant] : undefined)
      }
    >
      {label ?? "Continue"}
    </Button>
  );
}

export { ContinueButton };
